import { Product, Industry, CompanyStat, SelectionMatrixRow } from '@/lib/index';
import { Factory, Building2, Car, Pickaxe, Zap, Cpu, Pill, Plane } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

/* 产品详情页路由映射 */
const PRODUCT_DETAIL_ROUTES: Record<string, string> = {
  'fkm': '/products/fkm',
  'epdm': '/products/epdm',
  'nbr': '/products/nbr',
  'nr': '/products/nr',
  'antistatic': '/products/esd',
  'cr': '/products/cr',
};

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Factory,
  Building2,
  Car,
  Pickaxe,
  Zap,
  Cpu,
  Pill,
  Plane,
};

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const detailPath = PRODUCT_DETAIL_ROUTES[product.id] || '/products';
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -4 }}
      className="group relative overflow-hidden rounded h-full transition-all duration-300"
    >
      <Link
        to={detailPath}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          background: '#FFFFFF',
          border: '1px solid #D0D5E2',
          borderRadius: '0.25rem',
          textDecoration: 'none',
          color: 'inherit',
          position: 'relative',
          overflow: 'hidden',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.borderColor = 'oklch(0.66 0.19 42)';
          (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(230,115,0,0.12)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.borderColor = '#D0D5E2';
          (e.currentTarget as HTMLElement).style.boxShadow = 'none';
        }}
      >
        {/* Top accent bar */}
        <div
          className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ background: 'linear-gradient(90deg, oklch(0.66 0.19 42), transparent)', pointerEvents: 'none' }}
        />

        <div className="p-6 flex flex-col h-full">
          {/* Header */}
          <div className="mb-5">
            <span
              className="inline-block text-xs font-mono font-bold tracking-wider px-2 py-1 rounded-sm mb-3"
              style={{
                color: 'oklch(0.66 0.19 42)',
                background: 'rgba(230,115,0,0.10)',
                border: '1px solid rgba(230,115,0,0.3)',
              }}
            >
              {product.code}
            </span>
            <h3 className="text-lg font-bold text-foreground mb-1 leading-tight">{product.name}</h3>
            <p
              className="text-xs font-mono tracking-[0.15em] uppercase"
              style={{ color: '#5A6780' }}
            >
              {product.nameEn}
            </p>
          </div>

          {/* Params */}
          <div
            className="mb-5 p-3 rounded-sm space-y-2"
            style={{ background: '#F0F2F5', border: '1px solid #D0D5E2' }}
          >
            <div className="flex items-center justify-between text-xs">
              <span style={{ color: '#5A6780' }}>温度范围 / TEMP</span>
              <span className="font-mono font-bold text-foreground">{product.tempRange}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span style={{ color: '#5A6780' }}>拉伸强度 / TENSILE</span>
              <span className="font-mono font-bold text-foreground">{product.tensile}</span>
            </div>
          </div>

          {/* Applications */}
          <div className="mb-5 flex-1">
            <p
              className="text-[10px] font-bold tracking-[0.15em] uppercase mb-2"
              style={{ color: '#5A6780' }}
            >
              核心应用
            </p>
            <ul className="space-y-1.5">
              {product.applications.slice(0, 3).map((app, idx) => (
                <li key={idx} className="text-xs text-foreground flex items-start gap-2">
                  <span
                    className="flex-shrink-0 mt-1.5 w-1 h-1 rounded-full"
                    style={{ background: 'oklch(0.66 0.19 42)' }}
                  />
                  <span>{app}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div
            className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase"
            style={{ color: 'oklch(0.66 0.19 42)' }}
          >
            了解详情
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

interface IndustryCardProps {
  industry: Industry;
}

export function IndustryCard({ industry }: IndustryCardProps) {
  const IconComponent = iconMap[industry.icon];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -4 }}
      className="group relative overflow-hidden rounded flex flex-col transition-all duration-300 p-6"
      style={{
        background: '#FFFFFF',
        border: '1px solid #D0D5E2',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = 'oklch(0.66 0.19 42)';
        (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(230,115,0,0.12)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = '#D0D5E2';
        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
      }}
    >
      {/* Accent corner */}
      <div
        className="absolute top-0 right-0 w-12 h-12 opacity-10 group-hover:opacity-20 transition-opacity"
        style={{ background: 'oklch(0.66 0.19 42)' }}
        aria-hidden
      />

      <div className="flex flex-col items-center text-center">
        {IconComponent && (
          <div
            className="w-14 h-14 rounded flex items-center justify-center mb-4"
            style={{
              background: 'oklch(0.66 0.19 42 / 0.12)',
              border: '1px solid oklch(0.66 0.19 42 / 0.25)',
            }}
          >
            <IconComponent className="w-7 h-7 text-amber-500" />
          </div>
        )}
        <h3 className="text-base font-bold text-foreground mb-1">{industry.name}</h3>
        <p
          className="text-[10px] font-mono tracking-widest uppercase mb-3"
          style={{ color: '#5A6780' }}
        >
          {industry.nameEn}
        </p>
        <p className="text-xs text-muted-foreground leading-relaxed">{industry.description}</p>
      </div>
    </motion.div>
  );
}

interface StatCardProps {
  stat: CompanyStat;
}

export function StatCard({ stat }: StatCardProps) {
  // Detect if value is a short numeric badge (e.g. "20+", "8+") or text (e.g. "ISO 9001")
  const isNumeric = /^[\d.+]+$/.test(stat.value.replace(/\s/g, ''));

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="text-center p-4 rounded flex flex-col items-center justify-center"
      style={{
        background: 'rgba(255,255,255,0.85)',
        border: '1px solid rgba(255,255,255,0.3)',
        minHeight: '7rem',
      }}
    >
      {isNumeric ? (
        // Numeric stats: large bold number
        <div
          className="text-4xl md:text-5xl font-black mb-2 leading-none font-mono"
          style={{ color: 'oklch(0.66 0.19 42)' }}
        >
          {stat.value}
        </div>
      ) : (
        // Text badge (ISO 9001): fixed-size box matching numeric visual weight
        <div
          className="mb-2 flex items-center justify-center"
          style={{ height: '3rem' }}
        >
          <span
            className="font-black font-mono leading-tight text-center"
            style={{
              color: 'oklch(0.66 0.19 42)',
              fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
              letterSpacing: '0.04em',
              lineHeight: 1.15,
            }}
          >
            {stat.value}
          </span>
        </div>
      )}
      <div className="text-sm font-semibold mb-0.5" style={{ color: '#FFFFFF' }}>{stat.label}</div>
      <div
        className="text-[10px] font-mono tracking-[0.15em] uppercase"
        style={{ color: 'rgba(255,255,255,0.65)' }}
      >
        {stat.labelEn}
      </div>
    </motion.div>
  );
}

interface MatrixRowProps {
  row: SelectionMatrixRow;
  index: number;
}

export function MatrixRow({ row, index }: MatrixRowProps) {
  return (
    <tr
      className="group transition-colors"
      style={{
        background: index % 2 === 0 ? '#FFFFFF' : '#F7F8FA',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = '#EDF0F5';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background =
          index % 2 === 0 ? '#FFFFFF' : '#F7F8FA';
      }}
    >
      <td
        className="px-6 py-4 text-sm font-semibold text-foreground whitespace-nowrap"
        style={{ borderBottom: '1px solid #D0D5E2' }}
      >
        {row.productName}
      </td>
      <td
        className="px-6 py-4 text-center"
        style={{ borderBottom: '1px solid #D0D5E2' }}
      >
        <span
          className="text-xs font-mono font-bold px-2 py-0.5 rounded-sm"
          style={{
            color: 'oklch(0.66 0.19 42)',
            background: 'rgba(230,115,0,0.10)',
          }}
        >
          {row.productCode}
        </span>
      </td>
      <td
        className="px-6 py-4 text-sm font-mono text-center text-foreground"
        style={{ borderBottom: '1px solid #D0D5E2' }}
      >
        {row.tempRange}
      </td>
      <td
        className="px-6 py-4 text-sm font-mono text-center text-foreground"
        style={{ borderBottom: '1px solid #D0D5E2' }}
      >
        {row.tensile}
      </td>
      <td
        className="px-6 py-4 text-sm font-mono text-center text-foreground"
        style={{ borderBottom: '1px solid #D0D5E2' }}
      >
        {row.elongation}
      </td>
      <td
        className="px-6 py-4 text-sm font-mono text-center text-foreground"
        style={{ borderBottom: '1px solid #D0D5E2' }}
      >
        {row.hardness}
      </td>
      <td
        className="px-6 py-4 text-center"
        style={{ borderBottom: '1px solid #D0D5E2' }}
      >
        <span
          className="inline-block text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-sm"
          style={{
            color: 'oklch(0.66 0.19 42)',
            background: 'rgba(230,115,0,0.10)',
            border: '1px solid rgba(230,115,0,0.30)',
          }}
        >
          {row.keyFeature}
        </span>
      </td>
    </tr>
  );
}
